import Bugsnag from '@bugsnag/js'
import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'
import {
  Address,
  Airport,
  AuthenticatedUser,
  ConnectingAirport,
  LuggageRequirements,
  OversizeLuggageRequirements,
  PassengerRequirements,
  Transfer,
  TransferDirection,
} from '../../types'
import { TransferSearchQuery, TransferSearchResult } from '../../hooks/useTransfers/types'
import blankTransferSearchQuery from '../../hooks/useTransfers/blankTransferSearchQuery'
import {
  pluckAddress,
  pluckAirport,
  pluckConnectingAirport,
  pluckLuggageRequirements,
  pluckOversizedLuggageRequirements,
  pluckPassengerRequirements,
  pluckReverseDirection,
  swapAirportAndConnectingAirport,
} from '../../common'
import { Disclaimer } from '../../hooks/useDisclaimers'
import { NotifiableError } from '@bugsnag/core/types/common'

export interface AppState {
  authenticated: boolean
  token?: string
  user?: AuthenticatedUser

  transferSearchQuery: TransferSearchQuery

  transfers: Record<string, Transfer>
  disclaimers: Record<string, Disclaimer[]>
  transferBookingIds: string[]
  lastOrderId: string
}

export const initialState: AppState = {
  authenticated: false,
  token: undefined,
  user: undefined,

  transferSearchQuery: {
    ...blankTransferSearchQuery,
  },

  transfers: {},
  disclaimers: {},
  transferBookingIds: [],
  lastOrderId: '',
}

export const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    login(state: AppState, action: PayloadAction<AuthenticatedUser & { token: string }>) {
      const { token, ...user } = action.payload

      state.authenticated = true
      state.token = token
      state.user = user
    },

    updateUserProfile(state: AppState, action: PayloadAction<AuthenticatedUser>) {
      state.user = action.payload
    },

    logout(state: AppState) {
      state.authenticated = false
      state.token = initialState.token
      state.user = initialState.user
    },

    logError(state: AppState, action: PayloadAction<unknown>) {
      Bugsnag.notify(action.payload as NotifiableError)
    },

    startNewTransfer(state: AppState, action: PayloadAction<Airport | undefined>) {
      state.transferSearchQuery = {
        ...blankTransferSearchQuery,
        ...action.payload,
      }
    },

    startNewTransferWithSameRequirements(state: AppState) {
      const transfer = state.transfers[state.transferBookingIds[0]]

      state.transferSearchQuery = {
        ...blankTransferSearchQuery,
        ...pluckLuggageRequirements(transfer),
        ...pluckOversizedLuggageRequirements(transfer),
        ...pluckPassengerRequirements(transfer),
      }
    },

    startFromDestinationAirport(state: AppState) {
      const transfer = state.transfers[state.transferBookingIds[0]]

      state.transferSearchQuery = {
        ...blankTransferSearchQuery,
        ...pluckAddress(transfer),
        ...swapAirportAndConnectingAirport(transfer),
        ...pluckLuggageRequirements(transfer),
        ...pluckOversizedLuggageRequirements(transfer),
        ...pluckPassengerRequirements(transfer),
        direction: pluckReverseDirection(transfer.direction),
      }
    },

    startReverseTransfer(state: AppState) {
      const transfer = state.transfers[state.transferBookingIds[0]]

      state.transferSearchQuery = {
        ...blankTransferSearchQuery,
        ...pluckAddress(transfer),
        ...pluckAirport(transfer),
        ...pluckConnectingAirport(transfer),
        ...pluckLuggageRequirements(transfer),
        ...pluckOversizedLuggageRequirements(transfer),
        ...pluckPassengerRequirements(transfer),
        direction: pluckReverseDirection(transfer.direction),
      }
    },

    updateTransferSearchQuery(
      state: AppState,
      action: PayloadAction<
        | Address
        | Airport
        | ConnectingAirport
        | LuggageRequirements
        | PassengerRequirements
        | OversizeLuggageRequirements
        | { direction: TransferDirection }
        | { flight_number: string }
      >
    ) {
      state.transferSearchQuery = { ...state.transferSearchQuery, ...action.payload }
    },

    setDirection(state: AppState, action: PayloadAction<TransferDirection>) {
      state.transferSearchQuery.direction = action.payload
    },

    swapDirection(state: AppState) {
      state.transferSearchQuery.direction =
        state.transferSearchQuery.direction === TransferDirection.ToAirport
          ? TransferDirection.ToAddress
          : TransferDirection.ToAirport
    },

    updatePickupDate(state: AppState, action: PayloadAction<string>) {
      state.transferSearchQuery.pickup_datetime = action.payload
    },

    recordDisclaimers(state: AppState, action: PayloadAction<{ bookingId: string; disclaimers: Disclaimer[] }>) {
      const { bookingId, disclaimers } = action.payload

      state.disclaimers[bookingId] = disclaimers
    },

    selectTransfer(state: AppState, action: PayloadAction<TransferSearchResult>) {
      const { booking_id, session_id, price, provider } = action.payload

      state.transferBookingIds.unshift(booking_id)
      state.transfers[booking_id] = {
        ...state.transferSearchQuery,
        booking_id,
        session_id,
        provider,
        total: price,
      }

      state.transferSearchQuery = {
        ...blankTransferSearchQuery,
      }
    },

    removeTransfer(state: AppState, action: PayloadAction<string>) {
      delete state.transfers[action.payload]
      delete state.disclaimers[action.payload]
      state.transferBookingIds = state.transferBookingIds.filter((x) => x !== action.payload)
    },

    placeOrder(state: AppState, action: PayloadAction<string>) {
      state.lastOrderId = action.payload
      state.transfers = {}
      state.disclaimers = {}
      state.transferBookingIds = []
      state.transferSearchQuery = {
        ...blankTransferSearchQuery,
      }
    },
  },
})

export const {
  login,
  logout,
  logError,
  updateUserProfile,
  startNewTransfer,
  updateTransferSearchQuery,
  startReverseTransfer,
  startNewTransferWithSameRequirements,
  startFromDestinationAirport,
  setDirection,
  swapDirection,
  updatePickupDate,
  selectTransfer,
  recordDisclaimers,
  removeTransfer,
  placeOrder,
} = appSlice.actions

export default appSlice.reducer
